import { migrateProfileFromV1, remapProfileCorpusIds } from "./algorithm.js";
import { corpus } from "./corpus.js";
import { APP_VERSION, AppState, emptyAppState, MIN_LEVEL, Profile, STORAGE_KEY, SubjectProgress } from "./type.app.js";
import z from "zod";

export type StorageLike = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

const PersistedState = z.object({
  version: z.number().int(),
  passcode: z.string().nullable(),
  currentProfileId: z.string().nullable(),
  profiles: z.array(Profile),
  incomingShares: z.array(z.object({ ownerId: z.string(), profileId: z.string() })).optional(),
});

const progressFromProfile = (profile: Profile): SubjectProgress => ({
  level: profile.level,
  boostLevel: profile.boostLevel,
  correctStreak: profile.correctStreak,
  wrongStreak: profile.wrongStreak,
  maxCorrectStreak: profile.maxCorrectStreak,
  correctAnswers: Object.values(profile.textStats).reduce((sum, stat) => sum + stat.correct, 0),
  itemCorrect: {},
  mathSeen: {},
  mathWrong: {},
  mathHintHidden: {},
  mathReviewCount: 0,
  mathReviewInterval: 3,
  currentTextId: null,
  events: profile.events.map((event) => ({ result: event.result, at: event.at })),
});

export const normalizeSubjectProgress = (profile: Profile): Profile => {
  const subjectProgress = profile.subjectProgress ?? {
    reading: progressFromProfile(profile),
    math: {
      level: MIN_LEVEL,
      boostLevel: MIN_LEVEL,
      correctStreak: 0,
      wrongStreak: 0,
      maxCorrectStreak: 0,
      correctAnswers: 0,
      itemCorrect: {},
      mathSeen: {},
      mathWrong: {},
      mathHintHidden: {},
      mathReviewCount: 0,
      mathReviewInterval: 3,
      currentTextId: null,
      events: [],
    },
    spelling: {
      level: MIN_LEVEL,
      boostLevel: MIN_LEVEL,
      correctStreak: 0,
      wrongStreak: 0,
      maxCorrectStreak: 0,
      correctAnswers: 0,
      itemCorrect: {},
      mathSeen: {},
      mathWrong: {},
      mathHintHidden: {},
      mathReviewCount: 0,
      mathReviewInterval: 3,
      currentTextId: null,
      events: [],
    },
  };
  const reading = subjectProgress.reading;
  const math = subjectProgress.math ?? {
    level: MIN_LEVEL,
    boostLevel: MIN_LEVEL,
    correctStreak: 0,
    wrongStreak: 0,
    maxCorrectStreak: 0,
    correctAnswers: 0,
    itemCorrect: {},
    mathSeen: {},
    mathWrong: {},
    mathHintHidden: {},
    mathReviewCount: 0,
    mathReviewInterval: 3,
    currentTextId: null,
    events: [],
  };
  const spelling = subjectProgress.spelling ?? {
    level: MIN_LEVEL,
    boostLevel: MIN_LEVEL,
    correctStreak: 0,
    wrongStreak: 0,
    maxCorrectStreak: 0,
    correctAnswers: 0,
    itemCorrect: {},
    mathSeen: {},
    mathWrong: {},
    mathHintHidden: {},
    mathReviewCount: 0,
    mathReviewInterval: 3,
    currentTextId: null,
    events: [],
  };
  return {
    ...profile,
    selectedSubject: profile.selectedSubject ?? "reading",
    subjectProgress: { ...subjectProgress, math, spelling },
    level: reading.level,
    boostLevel: reading.boostLevel,
    correctStreak: reading.correctStreak,
    wrongStreak: reading.wrongStreak,
    maxCorrectStreak: reading.maxCorrectStreak,
  };
};

export const memoryStorage = (): StorageLike => {
  const data = new Map<string, string>();
  return {
    getItem: (key) => data.get(key) ?? null,
    setItem: (key, value) => {
      data.set(key, value);
    },
    removeItem: (key) => {
      data.delete(key);
    },
  };
};

export const migrateState = (raw: unknown): AppState => {
  const loose = PersistedState.parse(raw);
  const profiles = (loose.version < 2 ? loose.profiles.map(migrateProfileFromV1) : loose.profiles)
    .map(normalizeSubjectProgress)
    .map((profile) => remapProfileCorpusIds(profile, corpus));
  return AppState.parse({
    ...loose,
    version: APP_VERSION,
    profiles,
    incomingShares: loose.incomingShares ?? [],
  });
};

export const loadState = (storage: StorageLike): AppState => {
  return loadStateResult(storage).state;
};

export const loadStateResult = (storage: StorageLike): { state: AppState; valid: boolean } => {
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return { state: emptyAppState(), valid: true };
  try {
    return { state: migrateState(JSON.parse(raw)), valid: true };
  } catch {
    return { state: emptyAppState(), valid: false };
  }
};

export const parseAppDataJson = (raw: string): AppState => {
  return migrateState(JSON.parse(raw));
};

export const saveState = (storage: StorageLike, state: AppState): void => {
  storage.setItem(STORAGE_KEY, JSON.stringify(state));
};

export const clearState = (storage: StorageLike): void => {
  storage.removeItem(STORAGE_KEY);
};
